import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Button,
  Tabs,
  Tab,
  Alert,
  Stack,
  MenuItem,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getColorLabel } from '../utils/colors';
import AddressInput, { AddressValue } from '../components/AddressInput';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

interface BackendOrderItem {
  product_id: number | null;
  name: string;
  price: number;
  quantity: number;
  size?: string | null;
  color?: string | null;
}

interface BackendOrder {
  id: number;
  created_at: string;
  total_amount: number;
  shipping_amount?: number | null;
  items: BackendOrderItem[];
}

const Account: React.FC = () => {
  const {
    user,
    token,
    loading,
    error,
    login,
    register,
    verifyEmail,
    logout,
    updateProfile,
    requestPasswordReset,
    verifyResetCode,
    resetPassword,
  } = useAuth();

  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authLocalError, setAuthLocalError] = useState<string | null>(null);

  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const [resetStep, setResetStep] = useState<'request' | 'code' | 'new' | null>(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetNewPasswordConfirm, setResetNewPasswordConfirm] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);

  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileAddress1, setProfileAddress1] = useState('');
  const [profileAddress2, setProfileAddress2] = useState('');
  const [profileCity, setProfileCity] = useState('');
  const [profilePostalCode, setProfilePostalCode] = useState('');
  const [profileCountry, setProfileCountry] = useState('');
  const [paymentLabel, setPaymentLabel] = useState('');
  const [paymentDetailsDescription, setPaymentDetailsDescription] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileGender, setProfileGender] = useState('');
  const [profileBirthdate, setProfileBirthdate] = useState('');
  const [profileError, setProfileError] = useState<string | null>(null);

  const [orders, setOrders] = useState<BackendOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfilePhone(user.phone || '');
      setProfileAddress1(user.address_line1 || '');
      setProfileAddress2(user.address_line2 || '');
      setProfileCity(user.city || '');
      setProfilePostalCode(user.postal_code || '');
      setProfileCountry(user.country || 'BE');
      setPaymentLabel(user.payment_label || '');
      setPaymentDetailsDescription(
        (user.payment_details && (user.payment_details.description as string)) || ''
      );
      setProfileGender(user.gender || '');
      if (user.birthdate) {
        try {
          const d = new Date(user.birthdate as string);
          if (!Number.isNaN(d.getTime())) {
            setProfileBirthdate(d.toISOString().slice(0, 10));
          } else {
            setProfileBirthdate('');
          }
        } catch {
          setProfileBirthdate('');
        }
      } else {
        setProfileBirthdate('');
      }
    }
  }, [user]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) {
        setOrders([]);
        return;
      }

      setOrdersLoading(true);
      setOrdersError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/me/orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          if (res.status === 401) {
            setOrders([]);
            setOrdersError('Session expirée, merci de vous reconnecter.');
          } else {
            setOrdersError('Erreur lors du chargement des commandes.');
          }
          return;
        }

        const data = (await res.json()) as BackendOrder[];

        const normalized = Array.isArray(data)
          ? data.map((order) => ({
              ...order,
              total_amount: Number(order.total_amount) || 0,
              shipping_amount:
                order.shipping_amount != null
                  ? Number(order.shipping_amount)
                  : null,
            }))
          : [];

        setOrders(normalized);
      } catch (e) {
        console.error('Erreur lors de la récupération des commandes', e);
        setOrdersError('Erreur réseau lors du chargement des commandes.');
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  const handleProfileAddressChange = (addr: AddressValue) => {
    setProfileAddress1(addr.addressLine1);
    setProfileAddress2(addr.addressLine2);
    setProfileCity(addr.city);
    setProfilePostalCode(addr.postalCode);
    setProfileCountry(addr.country);
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLocalError(null);
    setResetError(null);
    setResetSuccess(null);
    const ok = await login(email, password);
    if (!ok) {
      setAuthLocalError('Identifiants invalides ou erreur lors de la connexion.');
    }
  };

  const handleForgotPasswordClick = () => {
    const baseEmail = email || resetEmail;
    setResetStep('request');
    setResetEmail(baseEmail);
    setResetCode('');
    setResetNewPassword('');
    setResetNewPasswordConfirm('');
    setResetError(null);
    setResetSuccess(null);
  };

  const handleResetRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(null);

    const emailToUse = resetEmail.trim() || email.trim();
    if (!emailToUse) {
      setResetError("Merci d'indiquer votre adresse e-mail.");
      return;
    }

    const ok = await requestPasswordReset(emailToUse);
    if (!ok) {
      return;
    }

    setResetEmail(emailToUse);
    setResetSuccess('Un code à 6 chiffres vous a été envoyé par e-mail.');
    setResetStep('code');
  };

  const handleResetCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(null);

    const emailToUse = resetEmail.trim();
    const code = resetCode.trim();

    if (!emailToUse || !code) {
      setResetError("Merci d'indiquer votre adresse e-mail et le code de réinitialisation.");
      return;
    }

    const ok = await verifyResetCode(emailToUse, code);
    if (!ok) {
      return;
    }

    setResetSuccess('Code correct. Vous pouvez maintenant choisir un nouveau mot de passe.');
    setResetStep('new');
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(null);

    const emailToUse = resetEmail.trim();
    const code = resetCode.trim();

    if (!emailToUse || !code) {
      setResetError("Informations de réinitialisation manquantes.");
      return;
    }

    if (!resetNewPassword || resetNewPassword.length < 6) {
      setResetError('Le nouveau mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    if (resetNewPassword !== resetNewPasswordConfirm) {
      setResetError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    const ok = await resetPassword(emailToUse, code, resetNewPassword);
    if (!ok) {
      return;
    }

    setResetSuccess('Votre mot de passe a été modifié. Vous pouvez maintenant vous connecter.');
    setResetStep(null);
    setPassword('');
    setEmail(emailToUse);
  };

  const handleCancelReset = () => {
    setResetStep(null);
    setResetError(null);
    setResetSuccess(null);
    setResetCode('');
    setResetNewPassword('');
    setResetNewPasswordConfirm('');
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLocalError(null);
    const ok = await register(email, password, name);
    if (!ok) {
      setAuthLocalError("Erreur lors de la création du compte.");
    } else {
      setIsVerifying(true);
      setVerificationEmail(email);
      setVerificationCode('');
      setVerificationError(null);
    }
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerificationError(null);
    const ok = await verifyEmail(verificationEmail, verificationCode);
    if (!ok) {
      setVerificationError('Code de vérification invalide ou expiré.');
    } else {
      setIsVerifying(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaved(false);
    setAuthLocalError(null);
    setProfileError(null);

    if (!profilePhone || !profileAddress1 || !profileCity || !profilePostalCode || !profileCountry) {
      setProfileError(
        'Merci de renseigner votre téléphone et votre adresse complète (rue, ville, code postal, pays) pour la livraison.'
      );
      return;
    }

    setSavingProfile(true);
    const ok = await updateProfile({
      name: profileName,
      phone: profilePhone,
      address_line1: profileAddress1,
      address_line2: profileAddress2,
      city: profileCity,
      postal_code: profilePostalCode,
      country: profileCountry,
      gender: profileGender || null,
      birthdate: profileBirthdate || null,
      payment_label: paymentLabel,
      payment_details: paymentDetailsDescription
        ? { description: paymentDetailsDescription }
        : null,
    });
    setSavingProfile(false);
    if (ok) {
      setProfileSaved(true);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 10 }}>
      <Container maxWidth="md" sx={{ py: 6 }}>
        {!user ? (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Mon compte
            </Typography>

            {!isVerifying ? (
              <>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{ mb: 3 }}
                  aria-label="Connexion ou création de compte"
                >
                  <Tab label="Connexion" value="login" />
                  <Tab label="Créer un compte" value="register" />
                </Tabs>

                {authLocalError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {authLocalError}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                {resetSuccess && !resetStep && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {resetSuccess}
                  </Alert>
                )}

                {tab === 'login' ? (
                  resetStep ? (
                    <>
                      {resetError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {resetError}
                        </Alert>
                      )}
                      {resetSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {resetSuccess}
                        </Alert>
                      )}

                      {resetStep === 'request' && (
                        <Box
                          component="form"
                          onSubmit={handleResetRequestSubmit}
                          noValidate
                          sx={{ mt: 1 }}
                        >
                          <Typography variant="h6" gutterBottom>
                            Mot de passe oublié
                          </Typography>
                          <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="reset_email"
                            label="Adresse e-mail"
                            name="reset_email"
                            autoComplete="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                          />
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ mt: 3 }}
                            disabled={loading}
                          >
                            Envoyer le code
                          </Button>
                          <Button
                            fullWidth
                            variant="text"
                            sx={{ mt: 1 }}
                            onClick={handleCancelReset}
                          >
                            Retour à la connexion
                          </Button>
                        </Box>
                      )}

                      {resetStep === 'code' && (
                        <Box
                          component="form"
                          onSubmit={handleResetCodeSubmit}
                          noValidate
                          sx={{ mt: 1 }}
                        >
                          <Typography variant="h6" gutterBottom>
                            Code de réinitialisation
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Un code à 6 chiffres a été envoyé à l'adresse {resetEmail}.
                          </Typography>
                          <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="reset_code"
                            label="Code de réinitialisation"
                            name="reset_code"
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value)}
                            inputProps={{ maxLength: 6 }}
                          />
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ mt: 3 }}
                            disabled={loading}
                          >
                            Valider le code
                          </Button>
                          <Button
                            fullWidth
                            variant="text"
                            sx={{ mt: 1 }}
                            onClick={handleCancelReset}
                          >
                            Retour à la connexion
                          </Button>
                        </Box>
                      )}

                      {resetStep === 'new' && (
                        <Box
                          component="form"
                          onSubmit={handleResetPasswordSubmit}
                          noValidate
                          sx={{ mt: 1 }}
                        >
                          <Typography variant="h6" gutterBottom>
                            Nouveau mot de passe
                          </Typography>
                          <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="new_password"
                            label="Nouveau mot de passe"
                            type="password"
                            id="new_password"
                            autoComplete="new-password"
                            value={resetNewPassword}
                            onChange={(e) => setResetNewPassword(e.target.value)}
                          />
                          <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="new_password_confirm"
                            label="Confirmer le mot de passe"
                            type="password"
                            id="new_password_confirm"
                            autoComplete="new-password"
                            value={resetNewPasswordConfirm}
                            onChange={(e) => setResetNewPasswordConfirm(e.target.value)}
                          />
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ mt: 3 }}
                            disabled={loading}
                          >
                            Enregistrer le nouveau mot de passe
                          </Button>
                          <Button
                            fullWidth
                            variant="text"
                            sx={{ mt: 1 }}
                            onClick={handleCancelReset}
                          >
                            Retour à la connexion
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box component="form" onSubmit={handleLoginSubmit} noValidate sx={{ mt: 1 }}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Adresse e-mail"
                        name="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Mot de passe"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 3 }}
                        disabled={loading}
                      >
                        Se connecter
                      </Button>
                      <Button
                        type="button"
                        fullWidth
                        variant="text"
                        sx={{ mt: 1 }}
                        onClick={handleForgotPasswordClick}
                      >
                        Mot de passe oublié ?
                      </Button>
                    </Box>
                  )
                ) : (
                  <Box component="form" onSubmit={handleRegisterSubmit} noValidate sx={{ mt: 1 }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="name"
                      label="Nom complet"
                      name="name"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Adresse e-mail"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Mot de passe"
                      type="password"
                      id="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{ mt: 3 }}
                      disabled={loading}
                    >
                      Créer mon compte
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box component="form" onSubmit={handleVerifySubmit} noValidate sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Vérification de votre compte
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Un code à 6 chiffres a été envoyé à l'adresse&nbsp;
                  <strong>{verificationEmail}</strong>. Veuillez le saisir ci-dessous pour
                  activer votre compte. Si vous ne voyez pas l'e-mail dans votre boîte de
                  réception, pensez à vérifier vos spams / courriers indésirables.
                </Typography>

                {verificationError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {verificationError}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="verification_code"
                  label="Code de vérification"
                  name="verification_code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  inputProps={{ maxLength: 6 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3 }}
                  disabled={loading}
                >
                  Valider le code
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h4" component="h1">
                Mon compte
              </Typography>
              <Button variant="outlined" color="inherit" onClick={logout}>
                Se déconnecter
              </Button>
            </Stack>

            <Box component="form" onSubmit={handleProfileSubmit} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Mes informations
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {profileError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {profileError}
                </Alert>
              )}
              {profileSaved && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Profil mis à jour avec succès.
                </Alert>
              )}

              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Nom"
                  fullWidth
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                />
                <TextField
                  label="Adresse e-mail"
                  fullWidth
                  value={user.email}
                  disabled
                />
                <TextField
                  label="Téléphone"
                  fullWidth
                  value={profilePhone}
                  onChange={(e) => setProfilePhone(e.target.value)}
                />
                <TextField
                  select
                  label="Sexe"
                  fullWidth
                  value={profileGender}
                  onChange={(e) => setProfileGender(e.target.value)}
                  disabled={Boolean(user?.gender)}
                >
                  <MenuItem value="">Non précisé</MenuItem>
                  <MenuItem value="homme">Homme</MenuItem>
                  <MenuItem value="femme">Femme</MenuItem>
                </TextField>
                <TextField
                  label="Date de naissance"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={profileBirthdate}
                  onChange={(e) => setProfileBirthdate(e.target.value)}
                  disabled={Boolean(user?.birthdate)}
                />
                <AddressInput
                  value={{
                    addressLine1: profileAddress1,
                    addressLine2: profileAddress2,
                    city: profileCity,
                    postalCode: profilePostalCode,
                    country: profileCountry || 'BE',
                    isManual: false,
                  }}
                  onChange={handleProfileAddressChange}
                  label="Adresse pour la livraison"
                />

                <Box sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={savingProfile}
                  >
                    Enregistrer mes informations
                  </Button>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Mes dernières commandes
              </Typography>

              {ordersLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Chargement de vos commandes...
                </Typography>
              ) : ordersError ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {ordersError}
                </Alert>
              ) : orders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Vous n'avez pas encore passé de commande.
                </Typography>
              ) : (
                <List>
                  {orders.map((order) => (
                    <React.Fragment key={order.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={`Commande n°${order.id} du ${new Date(
                            order.created_at
                          ).toLocaleDateString()}`}
                          secondary={
                            (() => {
                              const itemsTotal = (order.items || []).reduce(
                                (sum, item) => {
                                  const quantity =
                                    typeof item.quantity === 'number'
                                      ? item.quantity
                                      : Number(item.quantity) || 0;
                                  const price =
                                    typeof item.price === 'number'
                                      ? item.price
                                      : Number(item.price) || 0;
                                  return sum + quantity * price;
                                },
                                0
                              );

                              const hasShipping =
                                typeof order.shipping_amount === 'number' &&
                                Number.isFinite(order.shipping_amount);
                              const shippingAmount = hasShipping
                                ? order.shipping_amount || 0
                                : 0;

                              const rawDiscount =
                                itemsTotal + shippingAmount - order.total_amount;
                              const hasDiscount = rawDiscount > 0.01;
                              const discountAmount = hasDiscount ? rawDiscount : 0;

                              return (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    {order.items ? order.items.length : 0} article(s) — Total :{' '}
                                    {order.total_amount.toFixed(2)} €
                                  </Typography>
                                  {hasDiscount && (
                                    <Typography variant="body2" color="text.secondary">
                                      Réduction : <strong>-{discountAmount.toFixed(2)} €</strong>
                                    </Typography>
                                  )}
                                  {hasShipping && (
                                    <Typography variant="body2" color="text.secondary">
                                      Frais de livraison :{' '}
                                      <strong>{shippingAmount.toFixed(2)} €</strong>
                                    </Typography>
                                  )}
                                  {order.items && order.items.length > 0 && (
                                    <Box
                                      component="ul"
                                      sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}
                                    >
                                      {order.items.map((item, index) => {
                                        const quantity =
                                          typeof item.quantity === 'number'
                                            ? item.quantity
                                            : Number(item.quantity) || 0;
                                        const price =
                                          typeof item.price === 'number'
                                            ? item.price
                                            : Number(item.price) || 0;
                                        const lineTotal = quantity * price;

                                        return (
                                          <Box
                                            key={`${item.product_id || index}-${index}`}
                                            component="li"
                                            sx={{ mb: 0.25 }}
                                          >
                                            <Typography variant="body2">
                                              {item.name}
                                              {(item.size || item.color) && ' — '}
                                              {item.size && `Taille : ${item.size}`}
                                              {item.size && item.color && ' • '}
                                              {item.color &&
                                                `Couleur : ${getColorLabel(item.color)}`}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              x{quantity} — {lineTotal.toFixed(2)} €
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </>
                              );
                            })()
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Account;
